"use client";

import { useEffect, useState } from "react";
import { Menu, Search, X } from "lucide-react";
import { CartIcon } from "@/components/shop/CartIcon";
import { Link } from "@/components/common/Link";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        <div className="h-4 w-16 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-4 w-20 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-4 w-18 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-muted/40 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative text-foreground bg-transparent hover:bg-transparent focus:bg-transparent px-0 py-1 text-[13px] font-default uppercase tracking-[0.15em] font-medium transition-colors">
                <span className="relative">
                  {item.label}
                  <span className="absolute left-0 -bottom-1 h-px w-0 bg-foreground transition-all duration-300 group-hover:w-full" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 px-8 py-10">
        <div className="h-10 w-40 bg-muted/30 animate-pulse rounded-md" />
        <div className="h-10 w-48 bg-muted/30 animate-pulse rounded-md" />
        <div className="h-10 w-36 bg-muted/30 animate-pulse rounded-md" />
        <div className="h-10 w-44 bg-muted/30 animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-1 px-8 py-10">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onClose} className="text-foreground py-3 text-3xl font-default font-light tracking-tight border-b border-foreground/10 hover:text-foreground/70 transition-colors">
          {item.label}
        </Link>)}
    </nav>;
}
export default function MainNavigationHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="3436" id="main-navigation-header" className={`sticky top-0 z-50 w-full bg-background text-foreground transition-all duration-500 ${scrolled ? "bg-background/80 backdrop-blur-md border-b border-border py-3" : "py-6"}`}>
      <div className="mx-auto w-full max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-2 lg:grid-cols-3 items-center gap-4">
          {}
          <div className="flex flex-col items-start">
            
            <Link to="/HomePage" className="inline-flex items-center">
              <WvcLogo className="h-6 w-auto" />
            </Link>
          </div>

          {}
          <div className="hidden lg:flex justify-center">
            <MenuProvider menu_id="45" className="header-menu">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="flex items-center justify-end gap-2 lg:gap-4">
            <Button variant="ghost" size="icon" onClick={() => setSearchOpen(true)} className="hidden lg:inline-flex text-foreground hover:bg-accent" aria-label="Search resources">
              <Search className="size-[18px]" strokeWidth={1.5} />
            </Button>

            <Separator orientation="vertical" className="hidden lg:block h-5 bg-border" />

            <CartIcon variant="ghost" size="icon" iconSize="size-[18px]" className="text-foreground hover:bg-accent" />

            {}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden text-foreground hover:bg-accent" aria-label="Open menu">
                  <Menu className="size-5" strokeWidth={1.5} />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-full bg-background text-foreground border-l-0 p-0">
                <div className="flex items-center justify-between px-8 py-6 border-b border-border">
                  <span className="text-[11px] font-default uppercase tracking-[0.25em] text-foreground/60">
                    — MENU
                  </span>
                  <span className="text-[11px] font-default uppercase tracking-[0.25em] text-foreground/60">
                    FIT PET
                  </span>
                </div>
                <MenuProvider menu_id="45">
                  <MobileMenuConsumer onClose={() => setMobileOpen(false)} />
                </MenuProvider>
                <div className="px-8 py-6 mt-auto border-t border-border">
                  <Button variant="ghost" onClick={() => {
                  setMobileOpen(false);
                  setSearchOpen(true);
                }} className="w-full justify-start gap-3 text-foreground text-sm font-default uppercase tracking-[0.15em] hover:bg-accent px-0">
                    <Search className="size-4" strokeWidth={1.5} />
                    Search guides
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {}
      <Sheet open={searchOpen} onOpenChange={setSearchOpen}>
        <SheetContent side="top" className="bg-background text-foreground border-b border-border h-auto py-12 px-6 lg:px-10">
          <div className="mx-auto w-full max-w-3xl">
            <div className="flex items-center justify-between mb-6">
              <span className="text-[11px] font-default uppercase tracking-[0.25em] text-foreground/60">
                — SEARCH
              </span>
              <Button variant="ghost" size="icon" onClick={() => setSearchOpen(false)} className="text-foreground hover:bg-accent" aria-label="Close search">
                <X className="size-5" strokeWidth={1.5} />
              </Button>
            </div>
            <div className="relative">
              <Search className="absolute left-0 top-1/2 -translate-y-1/2 size-5 text-foreground/40" strokeWidth={1.5} />
              <input type="search" autoFocus placeholder="Search weight tips, trackers, or feeding guides..." className="w-full bg-transparent border-0 border-b border-border pl-9 pr-4 py-4 text-2xl md:text-3xl font-default font-light text-foreground placeholder:text-foreground/30 focus:outline-none focus:border-primary transition-colors" />
            </div>
            <p className="mt-4 text-xs font-default uppercase tracking-[0.2em] text-foreground/40">
              Press enter to search
            </p>
          </div>
        </SheetContent>
      </Sheet>
    </section>;
}